import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { createEventHook } from '@vueuse/core';
import { useI18n } from '@/plugins/i18n';
import { storeToRefs } from 'pinia';

import { PleasedApi } from '@/pleased-sdk/PleasedApi';
import { noop } from '@/common/utilities';
import {
  ChatTriggerActionType,
  ChatTriggerMatchSource,
  type MatchedChatTrigger,
  type ResolveTrigger,
} from '@/common/bot/flow-frameworks/chat-trigger';
import { useConfig } from '@/stores/useConfig';
import { TRANSACTION_TYPE_NAME_ENUM, type TransactionTypeName } from '@/common/transaction-utils';
import type { TransStatement } from '@/common/api/model';
import { TransactionType } from '@/common/config';
import type { PlsDialogAction } from '@/components/PlsDialog.types';
import { devLog } from '@/common/debug';

export interface TriggerMatch {
  report: {
    end: () => Promise<void>;
    continue: () => Promise<void>;
    openRequest: (ticketCreated: boolean) => Promise<void>;
  };
  message: string;
  chatTriggerId: number;
  actionType: {
    id: ChatTriggerActionType;
  };
  matchSource: MatchedChatTrigger['matchSource'];
  waitForResolution: MatchedChatTrigger['waitForResolution'];
}

export interface TriggerDialog {
  open: boolean;
  content: string;
  actions: PlsDialogAction[];
}

export const useDialogTrigger = defineStore('dialogTrigger', () => {
  const { t } = useI18n();
  const configStore = useConfig();
  const { config } = storeToRefs(configStore);

  const userCountry = computed(() => config.value?.country);

  /** Last overlay entity checked; transaction trigger matching reuses this context (see `ChatTrigger.checks`). */
  const lastOverlayTriggerContext = ref<{ type: ChatTriggerMatchSource; id: number } | null>(null);

  const matchTrigger = createEventHook<TriggerMatch>();
  const triggerDialog = ref<TriggerDialog | null>(null);
  const currentTrigger = ref<TriggerMatch | null>(null);

  /**
   * Creates a trigger report with resolution handling
   */
  function getChatTriggerReport({
    chatTriggerId,
    resolveTrigger,
  }: {
    chatTriggerId: number;
    resolveTrigger?: ResolveTrigger;
  }) {
    return {
      end: () =>
        PleasedApi.trackOverlyClick({ type: 'END_CHAT', chatTriggerId })
          .catch(noop)
          .then(() => resolveTrigger && resolveTrigger('END_CHAT')),
      continue: () =>
        PleasedApi.trackOverlyClick({ type: 'CONTINUE', chatTriggerId })
          .catch(noop)
          .then(() => resolveTrigger && resolveTrigger('CONTINUE')),
      openRequest: (ticketCreated: boolean) =>
        PleasedApi.trackOverlyClick({
          type: 'OPEN_REQUEST',
          chatTriggerId,
          ticketCreated,
        })
          .catch(noop)
          .then(() => resolveTrigger && resolveTrigger('CONTINUE')),
    };
  }

  const showTriggerDialog = computed(() => {
    return triggerDialog.value?.open && currentTrigger.value?.actionType.id === ChatTriggerActionType.SHOW_MODAL;
  });

  const showOpenRequestDialog = computed(() => {
    return currentTrigger.value?.actionType.id === ChatTriggerActionType.SHOW_OPEN_REQUEST_MODAL;
  });

  function closeOpenRequestDialog() {
    if (triggerDialog.value) {
      triggerDialog.value.open = false;
    }

    currentTrigger.value = null;
  }

  /**
   * Asynchronously runs the given promise `into` while simultaneously checking for any overlay triggers that need to be executed.
   * This will help in cases where
   */
  async function combineTriggerCheck<T>(
    params:
      | {
          type: ChatTriggerMatchSource;
          id: number | string;
          into: Promise<T>;
        }
      | {
          type: 'TRANSACTION';
          transactionType: TransactionTypeName;
          transaction: TransStatement;
          into: Promise<T>;
        }
  ): Promise<T> {
    if (params.type === 'TRANSACTION') {
      return Promise.all([
        params.into,
        checkTransactionTrigger({
          transactionType: params.transactionType,
          transaction: params.transaction,
        }),
      ]).then(([result]) => result);
    }

    return Promise.all([params.into, checkTrigger({ type: params.type, id: params.id })]).then(([result]) => result);
  }

  async function checkTrigger({
    type,
    id,
  }: {
    type: ChatTriggerMatchSource;
    id: number | string;
  }): Promise<void | TriggerMatch> {
    lastOverlayTriggerContext.value = { type, id: +id };

    return PleasedApi.matchOverlayTrigger
      .cached({ type, id: +id, userCountry: userCountry.value })
      .then((matchedTrigger) => {
        if (!matchedTrigger?.message?.length) return;
        const { chatTriggerId } = matchedTrigger;

        let resolveTrigger: ResolveTrigger | undefined;
        const triggerResolver = new Promise<'END_CHAT' | 'CONTINUE'>((resolve) => {
          resolveTrigger = resolve;
        });

        const trigger: TriggerMatch = {
          ...matchedTrigger,
          matchSource: type,
          report: getChatTriggerReport({ chatTriggerId, resolveTrigger }),
          waitForResolution: () => triggerResolver,
        };

        matchTrigger.trigger(trigger);

        return trigger;
      })
      .catch(noop);
  }

  /**
   * Runs `onContinue` when no overlay trigger matched, or after the user resolves
   * the trigger with CONTINUE (including open-request flows that resolve as CONTINUE).
   * Does not run `onContinue` when the user ends the chat (END_CHAT).
   */
  async function runAfterOverlayTriggerIfContinue(
    params: { type: ChatTriggerMatchSource; id: number | string },
    onContinue: () => void | Promise<void>
  ): Promise<void> {
    const trigger = await checkTrigger(params);
    if (!trigger) {
      await Promise.resolve(onContinue());
      return;
    }
    const outcome = await trigger.waitForResolution();
    if (outcome !== 'CONTINUE') {
      return;
    }
    await Promise.resolve(onContinue());
  }

  async function checkTransactionTrigger({
    transactionType,
    transaction,
  }: {
    transactionType: TransactionTypeName;
    transaction: TransStatement;
  }): Promise<void | TriggerMatch> {
    // Skip bet transactions
    if (transactionType === TRANSACTION_TYPE_NAME_ENUM[TransactionType.Bet]) {
      return Promise.resolve();
    }

    const overlayContext = lastOverlayTriggerContext.value;
    if (!overlayContext) {
      devLog.debug('[DIALOG_TRIGGER] No overlay trigger context available for transaction trigger check');
      return Promise.resolve();
    }

    return PleasedApi.matchTransactionTrigger({
      userCountry: userCountry.value || '',
      transactionType,
      transaction,
      type: overlayContext.type,
      id: overlayContext.id,
    })
      .then((matchedTrigger) => {
        if (!matchedTrigger?.message?.length) return;
        const { chatTriggerId } = matchedTrigger;

        let resolveTrigger: ResolveTrigger | undefined;
        const triggerResolver = new Promise<'END_CHAT' | 'CONTINUE'>((resolve) => {
          resolveTrigger = resolve;
        });

        const trigger: TriggerMatch = {
          ...matchedTrigger,
          report: getChatTriggerReport({ chatTriggerId, resolveTrigger }),
          waitForResolution: () => triggerResolver,
          matchSource: null,
        };

        matchTrigger.trigger(trigger);

        return trigger;
      })
      .catch(noop);
  }

  /**
   * Sets up the trigger dialog with end and continue actions.
   * Optionally accepts a custom onEnd callback for handling the end action.
   */
  function setupTriggerDialog(trigger: TriggerMatch, onEnd?: () => void | Promise<void>) {
    currentTrigger.value = trigger;

    // Open request dialog is handled separately
    if (trigger.actionType.id === ChatTriggerActionType.SHOW_OPEN_REQUEST_MODAL) {
      return;
    }

    const handleEnd = async () => {
      if (triggerDialog.value) {
        triggerDialog.value.open = false;
      }
      await trigger.report.end();
      if (onEnd) {
        await onEnd();
      }

      currentTrigger.value = null;
    };

    triggerDialog.value = {
      open: true,
      content: trigger.message,
      actions: [
        {
          id: 'end-chat',
          color: 'primary',
          text: t('global.end_chat') as string,
          action: handleEnd,
        },
        {
          id: 'continue',
          color: 'secondary',
          text: t('global.continue', 'Continue') as string,
          action: () => {
            trigger.report.continue();
            if (triggerDialog.value) {
              triggerDialog.value.open = false;
            }
            currentTrigger.value = null;
          },
        },
      ],
    };
  }

  return {
    triggerDialog,
    currentTrigger,
    showTriggerDialog,
    showOpenRequestDialog,

    checkTrigger,
    runAfterOverlayTriggerIfContinue,
    setupTriggerDialog,
    combineTriggerCheck,
    closeOpenRequestDialog,
    checkTransactionTrigger,

    onTriggerMatch: matchTrigger.on,
  };
});
